import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Edit, Trash2 } from "react-feather";
import Table from "../../core/pagination/datatable";
import PageHeader from "../components/PageHeader";
import confirmDelete from "../components/confirmDelete";
import PatientModal from "../../core/modals/patients/PatientModal";
import { fetchPatients, deletePatientThunk } from "../../core/redux/slices/patientSlice";
import { formatAge } from "../../core/utils/calculateAge";

// createdDate is an ISO timestamp; compare on its yyyy-mm-dd part.
const addedOn = (iso) => (iso ? iso.split("T")[0] : "");
const formatAddedOn = (iso) => { const d = addedOn(iso); if (!d) return "—"; const [y, m, day] = d.split("-"); return `${day}/${m}/${y}`; };

const PatientList = () => {
  const dispatch = useDispatch();
  const { list, loading } = useSelector((s) => s.patients);
  const [search, setSearch] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editRecord, setEditRecord] = useState(null);

  useEffect(() => { dispatch(fetchPatients()); }, [dispatch]);

  const data = useMemo(() => {
    const q = search.toLowerCase();
    return list
      .filter((p) =>
        !q || p.patientName?.toLowerCase().includes(q) || p.mobileNumber?.includes(q) || p.opNumber?.includes(q)
      )
      // Filter on the date the patient was added. A single date is from = to.
      .filter((p) => {
        if (!fromDate && !toDate) return true;
        const d = addedOn(p.createdDate);
        return !!d && (!fromDate || d >= fromDate) && (!toDate || d <= toDate);
      })
      .slice()
      .sort((a, b) => (b.createdDate || "").localeCompare(a.createdDate || ""));
  }, [list, search, fromDate, toDate]);

  const openAdd = () => { setEditRecord(null); setShowModal(true); };
  const openEdit = (rec) => { setEditRecord(rec); setShowModal(true); };
  const closeModal = () => { setShowModal(false); setEditRecord(null); dispatch(fetchPatients()); };

  const handleDelete = (rec) =>
    confirmDelete(() => dispatch(deletePatientThunk(rec.id)), rec.patientName);

  const columns = [
    { title: "OP No", dataIndex: "opNumber", sorter: (a, b) => (a.opNumber || "").localeCompare(b.opNumber || ""), width: 100 },
    { title: "Patient Name", dataIndex: "patientName", sorter: (a, b) => a.patientName.localeCompare(b.patientName) },
    { title: "Mobile", dataIndex: "mobileNumber" },
    { title: "DOB", dataIndex: "dob", render: (v) => v ? new Date(v).toLocaleDateString("en-IN") : "—" },
    { title: "Age", dataIndex: "age", width: 90, render: (v, rec) => formatAge(rec.dob, v) ?? "—" },
    { title: "Gender", dataIndex: "gender", width: 80 },
    { title: "Category", dataIndex: "category", width: 90,
      render: (v) => <span className={`badge bg-${v === "Arab" ? "warning" : v === "Custom" ? "info" : "success"} text-white`}>{v || "Normal"}</span> },
    { title: "Added On", dataIndex: "createdDate", width: 110, render: formatAddedOn,
      sorter: (a, b) => (a.createdDate || "").localeCompare(b.createdDate || "") },
    {
      title: "Action", dataIndex: "action", width: 100,
      render: (_, rec) => (
        <div className="action-table-data">
          <div className="edit-delete-action">
            <button className="me-2 p-2 btn btn-sm btn-icon" onClick={() => openEdit(rec)} title="Edit">
              <Edit size={15} className="feather-edit" />
            </button>
            <button className="p-2 btn btn-sm btn-icon text-danger" onClick={() => handleDelete(rec)} title="Delete">
              <Trash2 size={15} className="feather-trash-2" />
            </button>
          </div>
        </div>
      ),
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        <PageHeader title="Patients" subtitle="Manage patient records" onAdd={openAdd} addLabel="Add Patient" />
        <div className="card table-list-card">
          <div className="card-body">
            <div className="table-top">
              <div className="search-set">
                <div className="search-input">
                  <input type="text" placeholder="Search by name, mobile, OP No..."
                    className="form-control form-control-sm formsearch"
                    value={search} onChange={(e) => setSearch(e.target.value)} />
                </div>
              </div>
              <div className="d-flex align-items-center gap-2 flex-wrap">
                <label className="form-label small mb-0">Added from</label>
                <input type="date" className="form-control form-control-sm w-auto" value={fromDate}
                  max={toDate || undefined} onChange={(e) => setFromDate(e.target.value)} />
                <label className="form-label small mb-0">to</label>
                <input type="date" className="form-control form-control-sm w-auto" value={toDate}
                  min={fromDate || undefined} onChange={(e) => setToDate(e.target.value)} />
                {(fromDate || toDate) && (
                  <button type="button" className="btn btn-sm btn-outline-secondary"
                    onClick={() => { setFromDate(""); setToDate(""); }}>
                    Clear
                  </button>
                )}
              </div>
            </div>
            <div className="table-responsive">
              <Table columns={columns} dataSource={data} loading={loading} />
            </div>
          </div>
        </div>
      </div>
      <PatientModal show={showModal} onClose={closeModal} editRecord={editRecord} />
    </div>
  );
};

export default PatientList;
